/**
 * Demo component for Webhook Management Dashboard
 * Shows how to use the webhook management system with sample data
 */

import React, { useEffect, useState } from 'react';
import WebhookManagementDashboard from '../api/WebhookManagementDashboard';
import { webhookManager } from '../../services/webhooks/WebhookManager';
import { Play, Square, RefreshCw, Zap } from 'lucide-react';

const WebhookDashboardDemo: React.FC = () => {
  const [demoRunning, setDemoRunning] = useState(false);
  const [demoInterval, setDemoInterval] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Initialize demo webhooks
    initializeDemoData();
    
    return () => {
      if (demoInterval) {
        clearInterval(demoInterval);
      }
    };
  }, [demoInterval]);

  const initializeDemoData = async () => {
    try {
      // Register sample webhooks
      await webhookManager.registerWebhook({
        name: 'Salesforce Contact Sync',
        url: 'https://my-app.com/webhooks/salesforce/contacts',
        events: [
          { type: 'contact.created', description: 'New contact created' },
          { type: 'contact.updated', description: 'Contact information updated' },
          { type: 'sync.completed', description: 'Data synchronization completed' }
        ],
        active: true,
        integrationId: 'salesforce-demo',
        retryPolicy: {
          maxAttempts: 5,
          backoffMultiplier: 2,
          initialDelay: 1000,
          maxDelay: 300000,
          retryOn: [408, 429, 500, 502, 503, 504]
        }
      });

      await webhookManager.registerWebhook({
        name: 'Shopify Order Notifications',
        url: 'https://my-app.com/webhooks/shopify/orders',
        events: [
          { type: 'order.created', description: 'New order created' },
          { type: 'order.updated', description: 'Order status updated' },
          { type: 'cart.abandoned', description: 'Shopping cart abandoned' }
        ],
        active: true,
        integrationId: 'shopify-demo',
        retryPolicy: {
          maxAttempts: 3,
          backoffMultiplier: 1.5,
          initialDelay: 2000,
          maxDelay: 180000,
          retryOn: [500, 502, 503, 504]
        }
      });

      await webhookManager.registerWebhook({
        name: 'Campaign Analytics Webhook',
        url: 'https://analytics.example.com/webhooks/campaigns',
        events: [
          { type: 'campaign.sent', description: 'Email campaign sent' },
          { type: 'campaign.opened', description: 'Email campaign opened' },
          { type: 'campaign.clicked', description: 'Email campaign link clicked' }
        ],
        active: true,
        retryPolicy: {
          maxAttempts: 7,
          backoffMultiplier: 2,
          initialDelay: 500,
          maxDelay: 600000,
          retryOn: [408, 429, 500, 502, 503, 504]
        }
      });

      await webhookManager.registerWebhook({
        name: 'Error Monitoring Webhook',
        url: 'https://monitoring.example.com/webhooks/errors',
        events: [
          { type: 'error.occurred', description: 'Integration error occurred' },
          { type: 'sync.failed', description: 'Data synchronization failed' }
        ],
        active: false, // Inactive for demo
        retryPolicy: {
          maxAttempts: 10,
          backoffMultiplier: 1.8,
          initialDelay: 1500,
          maxDelay: 900000,
          retryOn: [408, 429, 500, 502, 503, 504]
        }
      });

      // Trigger some initial webhook deliveries for demo
      setTimeout(() => {
        simulateWebhookActivity();
      }, 2000);

    } catch (error) {
      console.error('Failed to initialize demo webhooks:', error);
    }
  };

  const simulateWebhookActivity = async () => {
    try {
      // Simulate some webhook events
      await webhookManager.triggerWebhook('contact.created', {
        id: 'contact_123',
        name: 'John Doe',
        email: 'john.doe@example.com',
        source: 'website_form',
        timestamp: new Date().toISOString()
      });

      await webhookManager.triggerWebhook('order.created', {
        id: 'order_456',
        customer_email: 'customer@example.com',
        total: 99.99,
        items: [
          { name: 'Product A', quantity: 2, price: 49.99 }
        ],
        timestamp: new Date().toISOString()
      });

      await webhookManager.triggerWebhook('campaign.sent', {
        campaign_id: 'camp_789',
        campaign_name: 'Summer Sale 2024',
        recipients: 15420,
        timestamp: new Date().toISOString()
      });

    } catch (error) {
      console.error('Failed to simulate webhook activity:', error);
    }
  };

  const startDemo = () => {
    if (demoRunning) return;
    
    setDemoRunning(true);
    
    // Simulate real-time webhook activity
    const interval = setInterval(() => {
      simulateRandomWebhookActivity();
    }, 8000); // Every 8 seconds
    
    setDemoInterval(interval);
  };

  const stopDemo = () => {
    if (demoInterval) {
      clearInterval(demoInterval);
      setDemoInterval(null);
    }
    setDemoRunning(false);
  };

  const simulateRandomWebhookActivity = async () => {
    const events = [
      {
        type: 'contact.updated',
        payload: {
          id: `contact_${Math.floor(Math.random() * 1000)}`,
          name: getRandomName(),
          email: `${getRandomName().toLowerCase().replace(' ', '.')}@example.com`,
          lead_score: Math.floor(Math.random() * 100),
          timestamp: new Date().toISOString()
        }
      },
      {
        type: 'order.created',
        payload: {
          id: `order_${Math.floor(Math.random() * 10000)}`,
          customer_email: `customer${Math.floor(Math.random() * 1000)}@example.com`,
          total: Math.round((Math.random() * 500 + 20) * 100) / 100,
          timestamp: new Date().toISOString()
        }
      },
      {
        type: 'campaign.opened',
        payload: {
          campaign_id: `camp_${Math.floor(Math.random() * 100)}`,
          recipient_email: `user${Math.floor(Math.random() * 1000)}@example.com`,
          timestamp: new Date().toISOString()
        }
      },
      {
        type: 'sync.completed',
        payload: {
          integration_id: 'salesforce-demo',
          records_synced: Math.floor(Math.random() * 500 + 50),
          duration_ms: Math.floor(Math.random() * 30000 + 5000),
          timestamp: new Date().toISOString()
        }
      },
      {
        type: 'cart.abandoned',
        payload: {
          cart_id: `cart_${Math.floor(Math.random() * 10000)}`,
          customer_email: `shopper${Math.floor(Math.random() * 1000)}@example.com`,
          cart_value: Math.round((Math.random() * 200 + 10) * 100) / 100,
          items_count: Math.floor(Math.random() * 5 + 1),
          timestamp: new Date().toISOString()
        }
      }
    ];

    const randomEvent = events[Math.floor(Math.random() * events.length)];
    
    try {
      await webhookManager.triggerWebhook(randomEvent.type, randomEvent.payload);
    } catch (error) {
      console.error('Failed to trigger webhook:', error);
    }
  };

  const getRandomName = (): string => {
    const firstNames = ['John', 'Jane', 'Mike', 'Sarah', 'David', 'Lisa', 'Chris', 'Emma', 'Alex', 'Maria'];
    const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez'];
    
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    
    return `${firstName} ${lastName}`;
  };

  const resetDemo = () => {
    stopDemo();
    // Clear existing webhooks and reinitialize
    setTimeout(() => {
      initializeDemoData();
    }, 1000);
  };

  const triggerTestWebhook = async () => {
    try {
      await webhookManager.triggerWebhook('contact.created', {
        id: `test_contact_${Date.now()}`,
        name: 'Test User',
        email: 'test@example.com',
        source: 'manual_test',
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to trigger test webhook:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-700">
      {/* Demo Controls */}
      <div className="bg-surface border-b border-line px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-content">Webhook Management Demo</h1>
            <p className="text-content-secondary mt-1">
              Real-time webhook registration, delivery tracking, and debugging tools
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${demoRunning ? 'bg-green-500' : 'bg-gray-400'}`}></div>
              <span className="text-sm text-content-secondary">
                Demo {demoRunning ? 'Running' : 'Stopped'}
              </span>
            </div>
            <button
              onClick={triggerTestWebhook}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <Zap className="w-4 h-4" />
              Test Webhook
            </button>
            <button
              onClick={demoRunning ? stopDemo : startDemo}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                demoRunning 
                  ? 'bg-red-600 text-white hover:bg-red-700' 
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {demoRunning ? (
                <>
                  <Square className="w-4 h-4" />
                  Stop Demo
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Start Demo
                </>
              )}
            </button>
            <button
              onClick={resetDemo}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Webhook Management Dashboard */}
      <WebhookManagementDashboard />
    </div>
  );
};

export default WebhookDashboardDemo;